import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { Form, Modal, Select, message } from "antd";
import { getDrivers } from "@/services/associatedUsers";
import { changeAssignedDriver } from "@/services/loads";
import SelectAutoComplete from "../SelectAutocomplete";

const { Option } = Select;

const ChangeDriverModal = ({ visible, loadData, refreshPageData, onClose: propOnClose }) => {
  const formRef = useRef(null);

  const [selectableDrivers, setSelectableDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const loadId = useMemo(() => {
    return loadData.id;
  }, [loadData?.id]);

  const onClose = useCallback(() => {
    formRef?.current?.resetFields();
    setLoadingSubmit(false);

    if (typeof propOnClose === "function") {
      propOnClose();
    }
  }, [propOnClose]);

  const handleSubmitChangeDriver = useCallback(() => {
    formRef?.current?.validateFields?.()?.then(result => {
      const { userId } = result;
      const { firstName, lastName } = selectableDrivers.find(userData => userData.userId === userId);

      setLoadingSubmit(true);
      changeAssignedDriver(loadId, userId)
        .then(() => {
          refreshPageData?.();
          message.success(`Successfully reassigned to ${firstName} ${lastName}`);
        })
        .finally(() => {
          onClose();
        });
    });
  }, [selectableDrivers, loadId, refreshPageData, onClose]);

  const fetchDrivers = useCallback(
    searchText => {
      return getDrivers({
        page: 1,
        pageSize: 10,
        filter: searchText,
        filterId: loadData.transporterId,
        shipmentIds: [loadData.shipmentId],
      });
    },
    [loadData],
  );

  useEffect(() => {
    if (!visible) {
      return;
    }

    setLoadingDrivers(true);
    fetchDrivers("")
      .then(response => {
        setSelectableDrivers(response?.items ?? []);
      })
      .catch(err => {
        console.error(err);
        message.error("Error fetching users");
      })
      .finally(() => {
        setLoadingDrivers(false);
      });
  }, [visible, fetchDrivers]);

  return (
    <Modal
      title="Change Assigned Driver"
      visible={visible}
      onOk={handleSubmitChangeDriver}
      onCancel={onClose}
      confirmLoading={loadingSubmit}
      afterClose={() => {
        formRef?.current?.resetFields();
      }}
    >
      <div>
        <span>
          This load is currently assigned to: <strong>{loadData.userFullName}</strong>
        </span>
      </div>
      <div style={{ marginBottom: 32 }}>Please select a new driver below to reassign the load.</div>
      <Form ref={formRef} layout="vertical">
        <Form.Item name="userId" label="New Driver" rules={[{ required: true }]}>
          <SelectAutoComplete
            placeholder="Select Driver"
            isLoading={loadingDrivers}
            options={selectableDrivers}
            fetchOptions={fetchDrivers}
            parseFetchedOptions={response => ({
              totalCount: response?.totalCount ?? 0,
              items: response?.items ?? [],
            })}
            renderOption={user => {
              return (
                <Option
                  value={user.userId}
                  key={`${user.firstName} ${user.lastName}` + user.id}
                  disabled={!user.isElegible}
                >
                  {`${user.firstName} ${user.lastName}`}
                </Option>
              );
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ChangeDriverModal;
